import React, { Fragment } from 'react';

import { ClockCircleOutlined, HeartOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Card, Col, Row } from 'antd';
import Meta from 'antd/lib/card/Meta';
import { Link } from 'react-router-dom';
import { MTag, CurrencyField, MTypography } from 'src/lib';
import { isOfferClosed, isRegDTypeCOffer, shouldDisplayTotalCapitalRaised } from 'src/utils';

import { OfferWatchDto } from '../../../../dtos/offerWatches.dtos';
import { MDivider } from '../../../../lib/MDivider/MDivider';
import { RemainingOfferDaysTag } from '../../../../lib/RemainingOfferDaysTag/RemainingOfferDaysTag';
import { Offer } from '../../../../models/offers.models';
import { Color, Image } from '../../../../styles';

import * as Styles from './styles';

export interface OfferCardProps {
  offer: Offer & { hasPendingOfferOrders: boolean };
  offerWatch?: OfferWatchDto;
  setIsWhatsAccreditedInvestorOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const OfferCard = ({ offer, offerWatch, setIsWhatsAccreditedInvestorOpen }: OfferCardProps) => {
  const coverCard = () => {
    return (
      <Fragment>
        <img
          alt='thumbnail'
          className={Styles.offerImage}
          src={
            offer?.videoThumbnailUrl ||
            offer?.assets[0]?.previewImageUrl ||
            offer?.assets[0]?.url ||
            Image.DefaultOffering
          }
        />
        <Row
          gutter={[
            { xs: 5, sm: 6, md: 8 },
            { xs: 5, sm: 6, md: 8 },
          ]}
          className={Styles.offerTags}>
          {!isOfferClosed(offer) && (
            <Fragment>
              {(offer.isComingSoon || offer.new) && (
                <Col>
                  <MTag type='lightblue' closable={false}>
                    {offer?.isComingSoon ? 'Coming Soon' : 'New'}
                  </MTag>
                </Col>
              )}
              <Col>
                <RemainingOfferDaysTag offer={offer} />
              </Col>
            </Fragment>
          )}
        </Row>
      </Fragment>
    );
  };

  const metaCard = () => {
    return (
      <Fragment>
        <Row>
          <Col span={24}>
            <MTypography variant='h6' weight='semibold' color={Color.Gray10} className={Styles.offerName}>
              {offer.name}
            </MTypography>
          </Col>
          <Col span={24}>
            <MTypography variant='body1' color={Color.Gray7}>
              {offer.industry}
            </MTypography>
          </Col>
          <Col span={24}>
            <MTypography variant='body1' color={Color.Gray7}>
              {offer.securityType}
            </MTypography>
          </Col>
        </Row>
        <Row className={Styles.offerSectionContainer}>
          {shouldDisplayTotalCapitalRaised(offer) ? (
            <Col span={24}>
              <MTypography variant='subtitle1' weight='bold' color={Color.Brand6}>
                <CurrencyField value={offer.totalCapitalRaised} />
              </MTypography>
            </Col>
          ) : (
            <Col span={24}>
              <MTypography variant='subtitle1' weight='bold' color={Color.Orange6}>
                <CurrencyField value={offer.minimumInvestCurrencyAmt} />
              </MTypography>
            </Col>
          )}
          <Col span={24}>
            <MTypography variant='body1' color={Color.Gray7}>
              {shouldDisplayTotalCapitalRaised(offer) ? 'Total Capital Raised' : 'Minimum Investment'}
            </MTypography>
          </Col>
        </Row>
        <MDivider />
        <Row
          gutter={[
            { xs: 5, sm: 6, md: 8 },
            { xs: 5, sm: 6, md: 8 },
          ]}>
          {offer.status && (
            <Col>
              <MTag type={isOfferClosed(offer) ? 'red' : 'green'}>{offer.status}</MTag>
            </Col>
          )}
          {offer.offerType && (
            <Col>
              <MTag type='gray'>{offer.offerType}</MTag>
            </Col>
          )}
          {isRegDTypeCOffer(offer) && (
            <Col>
              <MTag
                type='gray'
                closable={false}
                className={Styles.accreditedInvestorsOnly}
                onClick={event => {
                  event.preventDefault();
                  setIsWhatsAccreditedInvestorOpen(true);
                }}>
                <span className={Styles.accreditedInvestorsOnlyText}>Accredited Investors Only</span>{' '}
                <InfoCircleOutlined />
              </MTag>
            </Col>
          )}
          {offer?.isComingSoon && offerWatch && (
            <Col>
              <MTag type='lightgreen' icon={<HeartOutlined className={Styles.indicationLogo} />}>
                Interest Indicated
              </MTag>
            </Col>
          )}
          {offer?.hasPendingOfferOrders && (
            <Col>
              <MTag type='orange' icon={<ClockCircleOutlined />}>
                Pending Order
              </MTag>
            </Col>
          )}
        </Row>
      </Fragment>
    );
  };

  return (
    <Link key={offer.name} to={`/offers/${offer.shortUrlRoute ? offer.shortUrlRoute : offer.id}`}>
      <Card className={Styles.cardWrapper} cover={coverCard()}>
        <Meta title={metaCard()} />
      </Card>
    </Link>
  );
};

export default OfferCard;
